package data.datasource.remote

import data.datasource.GitHubRemoteDataSource
import domain.repository.AuthenticationRepository
import data.network.AuthorizedHttpClient
import data.model.GitHubSearchResponse
import domain.model.PullRequest
import domain.model.User
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch

/**
 * GitHub data source implementation using Clean Architecture
 * Uses AuthenticationRepository for token management and AuthenticatedHttpClient for requests
 */
class GitHubKtorRemoteDataSourceImpl(
    private val authRepository: AuthenticationRepository
) : GitHubRemoteDataSource {
    
    private val httpClient = AuthorizedHttpClient(authRepository)
    
    override fun checkAuthentication(callback: (Boolean) -> Unit) {
        GlobalScope.launch {
            try {
                val token = authRepository.getCurrentToken()
                callback(token != null)
            } catch (e: Exception) {
                println("❌ GitHubDS: Authentication check failed: ${e.message}")
                callback(false)
            }
        }
    }
    
    override fun fetchPullRequests(callback: (List<PullRequest>) -> Unit) {
        GlobalScope.launch {
            try {
                println("📋 GitHubDS: Fetching pull requests...")
                val pullRequests = getPullRequests()
                println("✅ GitHubDS: Successfully fetched ${pullRequests.size} pull requests")
                callback(pullRequests)
            } catch (e: Exception) {
                println("💥 GitHubDS: Error fetching pull requests: ${e.message}")
                callback(emptyList())
            }
        }
    }
    
    override fun initiateLogin() {
        authRepository.initiateLogin()
    }
    
    override fun logout() {
        GlobalScope.launch {
            try {
                authRepository.logout()
            } catch (e: Exception) {
                println("💥 GitHubDS: Error during logout: ${e.message}")
            }
        }
    }
    
    private suspend fun getPullRequests(): List<PullRequest> {
        try {
            println("🚀 GitHubDS: Making authenticated request to Vercel proxy...")
            
            val response = httpClient.authenticatedRequest {
                url("/api/auth/token")
                method = HttpMethod.Get
            }
            
            if (response.status == HttpStatusCode.OK) {
                // For now, return empty list since we're transitioning to GraphQL-based approach
                // The enhanced GitHub features will handle PR fetching via GraphQL
                println("✅ GitHubDS: Token validated, deferring to GraphQL implementation")
                return emptyList()
            } else {
                println("❌ GitHubDS: GitHub API error: ${response.status}")
                return emptyList()
            }
        } catch (e: Exception) {
            println("💥 GitHubDS: Error calling GitHub API: ${e.message}")
            // If it's an authentication error, it will be handled by the AuthenticatedHttpClient
            return emptyList()
        }
    }
}
